import React from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { 
    Facebook, 
    Linkedin, 
    Twitter, 
    Search, 
    User, 
    BookOpen, 
    Building2, 
    Send, 
    ShieldCheck 
} from 'lucide-react';

const Footer = () => {
    const { user } = useAuth();
    const navigate = useNavigate();

    const handleLoginRedirect = (e, path) => {
        e.preventDefault();
        if (!user) {
            alert("Please login first to access this feature.");
            navigate("/login");
        } else {
            navigate(path);
        }
    };

    const FooterSection = ({ title, links }) => (
        <div className="footer-section flex-1 min-w-[200px]">
            <h4 className="text-lg font-semibold mb-3 text-white">{title}</h4>
            <ul className="space-y-2">
                {links.map(({ label, path, icon: Icon }) => (
                    <li key={label}>
                        <Link 
                            to={path} 
                            onClick={(e) => handleLoginRedirect(e, path)}
                            className="flex items-center text-blue-100 hover:text-white transition text-base"
                        >
                            {Icon && <Icon className="mr-2 w-4 h-4" />}
                            {label}
                        </Link>
                    </li>
                ))}
            </ul>
        </div>
    );

    const isAdmin = user?.role === 'e2e-admin';
    const isRecruiter = user?.role === 'recruiter';

    const footerSections = [
        {
            title: "Job Seekers",
            links: [
                { label: "Search for jobs", path: "/job-search", icon: Search },
                // Only show profile for regular users (not e2e-admin or recruiter)
                ...(!isAdmin && !isRecruiter ? [
                    { label: "Your profile", path: "/profile", icon: User }
                ] : []),
                { label: "Career advice", path: "/career-advice", icon: BookOpen }
            ]
        },
        // Only show Employers section for recruiters (not for users or e2e-admin)
        ...(isRecruiter ? [{
            title: "Employers",
            links: [
                { label: "Employer site", path: "/employer-site", icon: Building2 },
                { label: "Post a job", path: "/create-job", icon: Send },
                { label: "Pricing", path: "/pricing", icon: ShieldCheck }
            ]
        }] : []),
        {
            title: "Connect",
            links: [
                { label: "Contact us", path: "/contact", icon: Send },
                { label: "About us", path: "/about" },
                { label: "Privacy policy", path: "/privacy" }
            ]
        }
    ];

    return (
        <footer className="bg-gradient-to-br from-blue-800 to-blue-900 text-white py-8 px-6 md:px-8">
            <div className="w-full px-2 md:px-4">

                <div className="footer-top grid md:grid-cols-3 gap-6 border-b border-blue-800 pb-6">
                    {footerSections.map((section) => (
                        <FooterSection 
                            key={section.title}
                            title={section.title} 
                            links={section.links}
                        />
                    ))}
                </div>
                
                <div className="footer-bottom flex flex-col md:flex-row justify-between items-center mt-6">
                    <p className="text-sm mb-3 md:mb-0"> 2024 YourCompany. All rights reserved.</p>
                    <div className="social-links flex gap-5">
                        {[
                            { Icon: Facebook, url: "https://www.facebook.com" },
                            { Icon: Linkedin, url: "https://www.linkedin.com" },
                            { Icon: Twitter, url: "https://www.twitter.com" }
                        ].map(({ Icon, url }) => (
                            <a
                                key={url}
                                href={url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-100 hover:text-white transition"
                            >
                                <Icon className="w-5 h-5" />
                            </a>
                        ))}
                    </div>
                </div>
            </div>
        </footer>
    );
};

export default Footer;